import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlan } from '../../components/plan/usePlan';
import { PHONE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';
import type { AssistantThreadRead } from '../../types/api';
import { useBusiness } from '../onboarding/useBusiness';
import { createAssistantThread } from './assistantApi';
import { readAssistantPrefs, writeAssistantPrefs, type AssistantPrefs } from './assistantStorage';
import { AssistantConversation } from './AssistantConversation';
import { AssistantCreditsMeter } from './AssistantCreditsMeter';
import { AssistantHistoryMenu } from './AssistantHistoryMenu';
import { useAssistantThread } from './useAssistantThread';
import { useClientActionRunner } from './useClientActionRunner';

/** Lucide glyphs from the vendored font (codepoints from its cmap). */
const GLYPH_MESSAGE = String.fromCodePoint(0xe116); // message-circle
const GLYPH_PLUS = String.fromCodePoint(0xe13d); // plus
const GLYPH_HISTORY = String.fromCodePoint(0xe1f5); // history
const GLYPH_EXPAND = String.fromCodePoint(0xe113); // maximize-2
const GLYPH_CLOSE = String.fromCodePoint(0xe1b2); // x

/** Routes where the bubble stands aside: the full page is the same conversation. */
function isDockHidden(pathname: string): boolean {
  return (
    pathname === '/assistant' || pathname.startsWith('/assistant/') || pathname === '/brands/new'
  );
}

/**
 * The assistant bubble and its panel, mounted once in `AppLayout` so it
 * survives navigation. Hidden on `/assistant` (the full page is the same
 * conversation), on `/brands/new` (a setup flow) and on a plan without the
 * assistant. Keyed by brand, so switching brands switches the remembered
 * thread and open state.
 */
export function AssistantDock() {
  const { pathname } = useLocation();
  const { business, businessId } = useBusiness();
  const { can, loading } = usePlan();
  // No launcher on a plan without the assistant, and none until the plan is
  // known, so it never appears and then vanishes.
  if (!businessId || isDockHidden(pathname) || loading || !can('assistant')) return null;
  return <Dock key={businessId} businessId={businessId} brandName={business?.name ?? ''} />;
}

interface DockProps {
  businessId: string;
  brandName: string;
}

function Dock({ businessId, brandName }: DockProps) {
  const navigate = useNavigate();
  const [prefs, setPrefsState] = useState<AssistantPrefs>(() => readAssistantPrefs(businessId));
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [seenArrivalId, setSeenArrivalId] = useState<string | null>(null);
  const [focusTick, setFocusTick] = useState(0);

  const bubbleRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();
  const panelId = useId();

  const chat = useAssistantThread(businessId, prefs.threadId);
  // On a phone the panel is the whole screen (the shell's phone tier).
  const isPhone = useMediaQuery(PHONE_QUERY);

  const setPrefs = useCallback(
    (update: (current: AssistantPrefs) => AssistantPrefs) => {
      setPrefsState((current) => {
        const next = update(current);
        writeAssistantPrefs(businessId, next);
        return next;
      });
    },
    [businessId],
  );

  const latestArrivalId = chat.latestArrivalId;
  const hasUnread = !prefs.open && latestArrivalId !== null && latestArrivalId !== seenArrivalId;
  const unreadArrival = hasUnread
    ? chat.messages.find((message) => message.id === latestArrivalId)
    : undefined;
  const isUnreadUpdate = unreadArrival?.kind === 'followup';
  // The panel's own live region is gone while it is shut: an update that lands
  // then is read out from here instead.
  const closedAnnouncement =
    unreadArrival === undefined
      ? ''
      : `${isUnreadUpdate ? 'AI assistant update' : 'AI assistant replied'}: ${unreadArrival.content.slice(0, 160)}`;

  const open = useCallback(() => {
    setSeenArrivalId(latestArrivalId);
    setPrefs((current) => ({ ...current, open: true }));
    setFocusTick((tick) => tick + 1);
  }, [latestArrivalId, setPrefs]);

  const close = useCallback(
    (returnFocus = true) => {
      setSeenArrivalId(latestArrivalId);
      setIsHistoryOpen(false);
      setPrefs((current) => ({ ...current, open: false }));
      if (returnFocus) bubbleRef.current?.focus();
    },
    [latestArrivalId, setPrefs],
  );

  // Focus moves to the composer when the user opens the panel (not when a
  // reload restores it open: that would steal focus from the page).
  useEffect(() => {
    if (focusTick > 0) composerRef.current?.focus();
  }, [focusTick]);

  // Ctrl+J toggles from anywhere in the shell, the composer included.
  const toggleRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    toggleRef.current = prefs.open ? () => close() : open;
  }, [prefs.open, open, close]);

  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'j' && event.key !== 'J') return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      // Chrome binds Ctrl+J to downloads; inside the app it is the assistant.
      event.preventDefault();
      toggleRef.current();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // A reply that opened a page: on a phone the panel covers it, so step aside.
  const onActionPerformed = useCallback(() => {
    if (isPhone) close(false);
  }, [close, isPhone]);
  useClientActionRunner(chat.messages, onActionPerformed);

  function onPanelKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return;
    // A dialog opened from inside the panel owns its own Escape.
    if (event.target instanceof Element && event.target.closest('dialog')) return;
    event.preventDefault();
    close();
  }

  function startNewChat() {
    setIsHistoryOpen(false);
    setPrefs((current) => ({ ...current, threadId: null }));
    composerRef.current?.focus();
  }

  function pickThread(threadId: string) {
    setIsHistoryOpen(false);
    setPrefs((current) => ({ ...current, threadId }));
    composerRef.current?.focus();
  }

  function expand() {
    const threadId = prefs.threadId !== null && !chat.notFound ? prefs.threadId : null;
    close(false);
    void navigate(threadId === null ? '/assistant?new=1' : `/assistant/${threadId}`);
  }

  function onThreadCreated(thread: AssistantThreadRead) {
    setPrefs((current) => ({ ...current, threadId: thread.id }));
  }

  const createThread = useCallback(() => createAssistantThread(businessId), [businessId]);
  const threadTitle = prefs.threadId !== null && !chat.notFound ? chat.thread?.title : null;

  return (
    <div className="assistant-dock">
      {prefs.open ? (
        <section
          className="assistant-panel"
          id={panelId}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          onKeyDown={onPanelKeyDown}
        >
          <header className="assistant-panel__head">
            <div className="assistant-panel__titles">
              <h2 className="assistant-panel__title" id={titleId}>
                AI assistant
              </h2>
              <p className="assistant-panel__sub">{threadTitle || brandName}</p>
              <AssistantCreditsMeter credits={chat.credits} variant="panel" />
            </div>
            <div className="assistant-panel__tools">
              <button
                className="assistant-panel__tool"
                type="button"
                title="New chat"
                aria-label="New chat"
                onClick={startNewChat}
              >
                <span className="assistant-glyph" aria-hidden="true">
                  {GLYPH_PLUS}
                </span>
              </button>
              <button
                className="assistant-panel__tool"
                type="button"
                title="Recent chats"
                aria-label="Recent chats"
                aria-expanded={isHistoryOpen}
                data-assistant-history-toggle=""
                onClick={() => setIsHistoryOpen((isOpen) => !isOpen)}
              >
                <span className="assistant-glyph" aria-hidden="true">
                  {GLYPH_HISTORY}
                </span>
              </button>
              <button
                className="assistant-panel__tool"
                type="button"
                title="Open full page"
                aria-label="Open full page"
                onClick={expand}
              >
                <span className="assistant-glyph" aria-hidden="true">
                  {GLYPH_EXPAND}
                </span>
              </button>
              <button
                className="assistant-panel__tool"
                type="button"
                title="Close (Esc)"
                aria-label="Close AI assistant"
                onClick={() => close()}
              >
                <span className="assistant-glyph" aria-hidden="true">
                  {GLYPH_CLOSE}
                </span>
              </button>
            </div>
            {isHistoryOpen ? (
              <AssistantHistoryMenu
                businessId={businessId}
                activeThreadId={prefs.threadId}
                onPick={pickThread}
                onClose={() => setIsHistoryOpen(false)}
                onOpenAll={() => close(false)}
              />
            ) : null}
          </header>

          <AssistantConversation
            businessId={businessId}
            threadId={prefs.threadId}
            chat={chat}
            createThread={createThread}
            onThreadCreated={onThreadCreated}
            variant="panel"
            composerRef={composerRef}
          />
        </section>
      ) : null}

      <button
        className={prefs.open ? 'assistant-bubble assistant-bubble--open' : 'assistant-bubble'}
        type="button"
        ref={bubbleRef}
        aria-label={
          prefs.open
            ? 'Close AI assistant'
            : hasUnread
              ? isUnreadUpdate
                ? 'Open AI assistant, new update'
                : 'Open AI assistant, new reply'
              : 'Open AI assistant'
        }
        aria-expanded={prefs.open}
        aria-controls={prefs.open ? panelId : undefined}
        title="AI assistant (Ctrl J)"
        onClick={() => (prefs.open ? close() : open())}
      >
        <span className="assistant-glyph assistant-bubble__glyph" aria-hidden="true">
          {GLYPH_MESSAGE}
        </span>
        {hasUnread ? <span className="assistant-bubble__dot" aria-hidden="true" /> : null}
      </button>

      <div className="visually-hidden" aria-live="polite">
        {prefs.open ? '' : closedAnnouncement}
      </div>
    </div>
  );
}
